import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import type { Database } from '@/lib/database.types';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Edit, Package, Plus, X, Trash2 } from 'lucide-react';
import { getConfig } from '@/config/environments';

type Producer = Database['public']['Tables']['producers']['Row'] & {
  profiles: {
    first_name: string | null;
    last_name: string | null;
    email: string;
  };
};

interface InviteFormData {
  email: string;
  companyName: string;
}

export function ProducersList() {
  const [producers, setProducers] = React.useState<Producer[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = React.useState(false);
  const [inviteForm, setInviteForm] = React.useState({
    email: '',
    companyName: ''
  });
  const [isInviting, setIsInviting] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    fetchProducers();
  }, []);

  async function fetchProducers() {
    try {
      const { data, error } = await supabase
        .from('producers')
        .select(`
          *,
          profiles:profiles(first_name, last_name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (data && data.length > 0) {
        setProducers(data);
      } else {
        setProducers([]);
      }
    } catch (err) {
      console.error('Error fetching producers:', err);
      setError('Failed to load producers');
    } finally {
      setLoading(false);
    }
  }

  const handleInvite = async (inviteForm: InviteFormData) => {
    try {
      setIsInviting(true);
      
      // Check if supabaseAdmin is available
      if (!supabaseAdmin) {
        throw new Error('Admin functionality is not available. Please check if VITE_SUPABASE_SERVICE_ROLE_KEY is properly configured.');
      }
      
      // Check if user already exists
      const { data: existingUser, error: userCheckError } = await supabaseAdmin
        .from('profiles')
        .select('id, email, role')
        .eq('email', inviteForm.email)
        .maybeSingle();
      
      if (userCheckError) {
        throw new Error(`Error checking existing user: ${userCheckError.message}`);
      }
      
      let userId;
      
      if (existingUser) {
        // User exists - update role if needed
        userId = existingUser.id;
        
        if (existingUser.role !== 'producer') {
          const { error: updateError } = await supabaseAdmin
            .from('profiles')
            .update({ role: 'producer' })
            .eq('id', userId);
            
          if (updateError) {
            throw new Error(`Failed to update user role: ${updateError.message}`);
          }
        }
      } else {
        // User doesn't exist - send invitation
        try {
          const { data: inviteData, error: inviteError } = await supabaseAdmin.auth.admin.inviteUserByEmail(
            inviteForm.email,
            {
              data: {},
              redirectTo: `${window.location.origin}/producer/onboarding`
            }
          );
          
          if (inviteError) {
            throw inviteError;
          }
          
          if (!inviteData.user) {
            throw new Error('No user data returned after invitation');
          }
          
          userId = inviteData.user.id;
        } catch (inviteErr) {
          console.error('Invitation failed, trying fallback method:', inviteErr);
          
          // Fallback: Create user with temporary password
          const tempPassword = Math.random().toString(36).slice(-10) + 
                              Math.random().toString(36).slice(-10);
          
          const { data: userData, error: createError } = await supabaseAdmin.auth.admin.createUser({
            email: inviteForm.email,
            password: tempPassword,
            email_confirm: true
          });
          
          if (createError) {
            throw new Error(`Failed to create user: ${createError.message}`);
          }
          
          if (!userData.user) {
            throw new Error('No user data returned after creation');
          }
          
          userId = userData.user.id;
          
          // Send password reset email so user can set their own password
          const { error: resetError } = await supabaseAdmin.auth.admin.generateLink({
            type: 'recovery',
            email: inviteForm.email,
            options: {
              redirectTo: `${window.location.origin}/producer/onboarding`
            }
          });
          
          if (resetError) {
            console.warn(`Warning: Could not send password reset: ${resetError.message}`);
            // Continue anyway as the user was created successfully
          }
        }
        
        // Create profile if doesn't exist
        try {
          // First check if the profile exists
          const { data: existingProfile } = await supabaseAdmin
            .from('profiles')
            .select('id')
            .eq('id', userId)
            .single();
          
          if (existingProfile) {
            // Update existing profile
            const { error: updateError } = await supabaseAdmin
              .from('profiles')
              .update({
                role: 'producer',
                email: inviteForm.email,
                updated_at: new Date().toISOString()
              })
              .eq('id', userId);
              
            if (updateError) {
              throw new Error(`Failed to update profile: ${updateError.message}`);
            }
          } else {
            // Create new profile
            const { error: insertError } = await supabaseAdmin
              .from('profiles')
              .insert({
                id: userId,
                email: inviteForm.email,
                role: 'producer',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
              
            if (insertError) {
              throw new Error(`Failed to create profile: ${insertError.message}`);
            }
          }
        } catch (profileError) {
          console.error('Error with profile:', profileError);
          throw new Error(`Failed to manage profile: ${profileError instanceof Error ? profileError.message : 'Unknown error'}`);
        }
        
        // Also update user metadata in auth.users
        const { error: metadataError } = await supabaseAdmin.auth.admin.updateUserById(
          userId,
          { 
            user_metadata: {
              company_name: inviteForm.companyName
            }
          }
        );
        
        if (metadataError) {
          console.warn(`Warning: Could not update user metadata: ${metadataError.message}`);
        }
      }

      // Create producer record with the company name provided
      const { error: producerError } = await supabaseAdmin
        .from('producers')
        .insert({
          id: userId,
          company_name: inviteForm.companyName,
          is_approved: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (producerError) {
        // If the producer already exists, just update the company name
        if (producerError.code === '23505') { // Unique violation
          const { error: updateProducerError } = await supabaseAdmin
            .from('producers')
            .update({
              company_name: inviteForm.companyName,
              updated_at: new Date().toISOString()
            })
            .eq('id', userId);

          if (updateProducerError) {
            throw new Error(`Failed to update producer record: ${updateProducerError.message}`);
          }
        } else {
          throw new Error(`Failed to create producer record: ${producerError.message}`);
        }
      }

      setIsInviteModalOpen(false);
      setInviteForm({ email: '', companyName: '' });
      toast.success('Producer invitation sent successfully!');
      
      // Refresh the producer list
      fetchProducers();
    } catch (err) {
      console.error('Error inviting producer:', err);
      toast.error(`Failed to invite producer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsInviting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await handleInvite({ email: inviteForm.email, companyName: inviteForm.companyName });
  };

  const toggleApprovalStatus = async (producerId: string, currentStatus: boolean) => {
    try {
      console.log(`Attempting to ${currentStatus ? 'disapprove' : 'approve'} producer ${producerId}`);
      
      // Get the current user's session for authorization
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        toast.error('Authentication error. Please log out and log in again.');
        return;
      }
      
      if (!session) {
        console.error('No session found');
        toast.error('You must be logged in to perform this action');
        return;
      }

      console.log('Session found:', {
        userId: session.user?.id,
        email: session.user?.email,
        tokenExpiry: session.expires_at,
        tokenLength: session.access_token?.length
      });

      // Verify user is admin before proceeding
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (profileError) {
        console.error('Profile error:', profileError);
        toast.error('Unable to verify user permissions');
        return;
      }

      if (!profile || profile.role !== 'admin') {
        console.error('User is not admin:', { profile });
        toast.error('Admin access required');
        return;
      }

      console.log('User verified as admin');

      let updateSuccess = false;
      let errorMessage = '';

      // Check if we're in development mode
      const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
      const currentEnv = import.meta.env.VITE_APP_ENV || 'development';
      
      console.log('Environment info:', {
        isDev,
        currentEnv,
        hasSupabaseAdmin: !!supabaseAdmin,
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        hasAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
      });
      
      // Try using supabaseAdmin first ONLY in development when available
      if (isDev && supabaseAdmin) {
        console.log('Using supabaseAdmin for direct database update (development mode)');
        
        const { error } = await supabaseAdmin
          .from('producers')
          .update({ 
            is_approved: !currentStatus,
            updated_at: new Date().toISOString() 
          })
          .eq('id', producerId);
          
        if (error) {
          console.error('Direct database error:', error);
          errorMessage = error.message;
        } else {
          updateSuccess = true;
        }
      }

      // Use Edge Function as primary method (for staging/production) or fallback (for development)
      if (!updateSuccess) {
        console.log('Using Edge Function for producer approval');
        
        // Use the correct Supabase Edge Function URL format from environment config
        const envConfig = getConfig();
        const supabaseUrl = envConfig.supabase.url;
        const anonKey = envConfig.supabase.anonKey;
        const functionUrl = `${supabaseUrl}/functions/v1/admin-producer-approval`;
        
        // Enhanced debugging
        console.log('=== DETAILED EDGE FUNCTION DEBUG ===');
        console.log('Environment configuration:');
        console.log('  Environment name:', envConfig.name);
        console.log('  Supabase URL from config:', supabaseUrl);
        console.log('  Anon key from config (first 50 chars):', anonKey?.substring(0, 50));
        console.log('  Function URL:', functionUrl);
        console.log('  Current hostname:', window.location.hostname);
        console.log('  Full current URL:', window.location.href);
        console.log('Environment variables (fallback):');
        console.log('  VITE_SUPABASE_URL:', import.meta.env.VITE_SUPABASE_URL);
        console.log('  VITE_SUPABASE_ANON_KEY (first 50 chars):', import.meta.env.VITE_SUPABASE_ANON_KEY?.substring(0, 50));
        console.log('=======================================');
        
        console.log('Edge Function request details:', {
          url: functionUrl,
          hasToken: !!session.access_token,
          hasAnonKey: !!anonKey,
          producerId,
          newStatus: !currentStatus
        });
        
        const requestBody = {
          producerId,
          isApproved: !currentStatus
        };
        
        console.log('Request body:', requestBody);
        
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': anonKey,
          'x-client-info': 'lokalaproducenter-admin'
        };
        
        console.log('Request headers:', Object.keys(headers));
        
        try {
          const response = await fetch(functionUrl, {
            method: 'PATCH',
            headers,
            body: JSON.stringify(requestBody),
          });

          console.log('Edge Function response status:', response.status);
          console.log('Response headers:', Object.fromEntries(response.headers.entries()));

          if (!response.ok) {
            let errorData;
            let responseText = '';
            try {
              responseText = await response.text();
              console.log('Raw response text:', responseText);
              errorData = JSON.parse(responseText);
            } catch (parseError) {
              console.error('Failed to parse error response:', parseError);
              errorData = { error: `HTTP error! status: ${response.status}, response: ${responseText}` };
            }
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          console.log('Edge Function success response:', result);
          updateSuccess = true;
        } catch (fetchError) {
          console.error('Fetch error details:', {
            error: fetchError,
            message: fetchError instanceof Error ? fetchError.message : 'Unknown fetch error',
            stack: fetchError instanceof Error ? fetchError.stack : undefined
          });
          throw fetchError;
        }
      }

      if (updateSuccess) {
        // Update local state to reflect the change immediately
        setProducers(prevProducers => 
          prevProducers.map(producer => 
            producer.id === producerId 
              ? { ...producer, is_approved: !currentStatus } 
              : producer
          )
        );
        
        const action = currentStatus ? 'unapproved' : 'approved';
        console.log(`Producer ${action} successfully`);
        toast.success(`Producer ${action} successfully`);
      } else {
        throw new Error(errorMessage || 'Failed to update producer status');
      }
    } catch (error) {
      console.error('Error toggling approval status:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Failed to update producer status: ${errorMessage}`);
    }
  };

  // New function to handle producer removal
  const handleRemoveProducer = async (producerId: string, producerName: string) => {
    if (!confirm(`Are you sure you want to remove ${producerName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setIsDeleting(true);
      
      // Check if supabaseAdmin is available
      if (!supabaseAdmin) {
        throw new Error('Admin functionality is not available. Please check if VITE_SUPABASE_SERVICE_ROLE_KEY is properly configured.');
      }
      
      // 1. First check if producer has any products
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('id')
        .eq('producer_id', producerId);
      
      if (productsError) {
        throw new Error(`Error checking producer products: ${productsError.message}`);
      }
      
      if (products && products.length > 0) {
        if (!confirm(`This producer has ${products.length} products in the system. Removing this producer will also delete all associated products. Continue?`)) {
          return;
        }
        
        // Delete all products first
        const { error: deleteProductsError } = await supabaseAdmin
          .from('products')
          .delete()
          .eq('producer_id', producerId);
        
        if (deleteProductsError) {
          throw new Error(`Error deleting producer's products: ${deleteProductsError.message}`);
        }
      }
      
      // 2. Delete the producer record
      const { error: deleteProducerError } = await supabaseAdmin
        .from('producers')
        .delete()
        .eq('id', producerId);
      
      if (deleteProducerError) {
        throw new Error(`Error removing producer: ${deleteProducerError.message}`);
      }
      
      // 3. Update the user profile to change role from 'producer' to 'customer'
      const { error: updateProfileError } = await supabaseAdmin
        .from('profiles')
        .update({ role: 'customer' })
        .eq('id', producerId);
      
      if (updateProfileError) {
        throw new Error(`Error updating user profile: ${updateProfileError.message}`);
      }
      
      toast.success(`Producer ${producerName} has been removed`);
      
      // 4. Refresh the producer list
      fetchProducers();
    } catch (err) {
      console.error('Error removing producer:', err);
      toast.error(`Failed to remove producer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Producers</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Plus className="h-5 w-5 mr-2" />
            Invite Producer
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Invite Modal */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-medium">Invite New Producer</h2>
              <button
                onClick={() => setIsInviteModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  placeholder="producer@example.com"
                />
              </div>
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <input
                  type="text"
                  id="companyName"
                  required
                  value={inviteForm.companyName}
                  onChange={(e) => setInviteForm({ ...inviteForm, companyName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  placeholder="Company Name"
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isInviting}
                  className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {isInviting ? 'Sending Invitation...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-6">
                Producer
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-900">
                Contact
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-medium text-gray-900">
                Website/Social
              </th>
              <th scope="col" className="px-3 py-3.5 text-center text-sm font-medium text-gray-900">
                Status
              </th>
              <th scope="col" className="px-3 py-3.5 text-center text-sm font-medium text-gray-900">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {producers.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  No producers found
                </td>
              </tr>
            ) : (
              producers.map((producer) => (
                <tr key={producer.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                    <div className="flex items-center">
                      {producer.logo_url ? (
                        <img
                          src={producer.logo_url}
                          alt=""
                          className="h-12 w-12 rounded-md object-contain bg-gray-50"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-md bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-500 font-medium text-lg">
                            {producer.company_name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="ml-4">
                        <div className="font-medium text-gray-900">
                          {producer.company_name}
                        </div>
                        <div className="text-gray-500 text-xs mt-1">
                          Joined {new Date(producer.created_at).toLocaleDateString()}
                        </div>
                        {producer.description && (
                          <div className="text-gray-500 text-xs mt-1 max-w-xs truncate">
                            {producer.description.replace(/<[^>]*>/g, '')}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    <div className="font-medium">
                      {producer.profiles ? (
                        <>
                          {producer.profiles.first_name || ''} {producer.profiles.last_name || ''}
                        </>
                      ) : (
                        <span className="italic">No contact data</span>
                      )}
                    </div>
                    {producer.profiles && (
                      <div className="text-xs">{producer.profiles.email}</div>
                    )}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    <div className="space-y-1">
                      {producer.website_url && (
                        <div className="text-xs">
                          <a href={producer.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Website
                          </a>
                        </div>
                      )}
                      {producer.facebook_url && (
                        <div className="text-xs">
                          <a href={producer.facebook_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Facebook
                          </a>
                        </div>
                      )}
                      {producer.instagram_url && (
                        <div className="text-xs">
                          <a href={producer.instagram_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Instagram
                          </a>
                        </div>
                      )}
                      {producer.twitter_url && (
                        <div className="text-xs">
                          <a href={producer.twitter_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Twitter
                          </a>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-center">
                    <span 
                      data-testid={`approve-${producer.id}`}
                      className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded cursor-pointer ${
                        producer.is_approved
                          ? 'text-green-700 bg-green-100 hover:bg-green-200'
                          : 'text-red-700 bg-red-100 hover:bg-red-200'
                      }`}
                      onClick={() => toggleApprovalStatus(producer.id, producer.is_approved)}
                    >
                      {producer.is_approved ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approved
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 mr-1" />
                          Not Approved
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-sm text-center">
                    <div className="flex justify-center space-x-2">
                      <Link
                        to={`/admin/producers/${producer.id}/edit`}
                        className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Link>
                      <Link
                        to={`/admin/producers/${producer.id}/products`}
                        className="text-green-600 hover:text-green-900 inline-flex items-center"
                      >
                        <Package className="h-4 w-4 mr-1" />
                        Products
                      </Link>
                      <button
                        className="icon-button delete"
                        onClick={() => handleRemoveProducer(producer.id, producer.company_name)}
                        disabled={isDeleting}
                        title="Remove Producer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
} 